<template>
    <div class="refund-error-block">
        <div class="block-header">
            <div class="error-icon">
                <svg class="w-16 h-16 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
            </div>
            <h3 class="block-title">Unable to Process Refund</h3>
            <p class="block-description">
                We encountered an issue while processing your refund request.
            </p>
        </div>

        <div class="error-details">
            <div class="info-card error-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Error Details</h4>
                </div>
                <div class="card-content">
                    <p class="error-message">{{ errorMessage }}</p>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">What You Can Do</h4>
                </div>
                <div class="card-content">
                    <ul class="action-list">
                        <li>Try submitting your refund request again</li>
                        <li>Check that your payment information is up to date</li>
                        <li>Contact Privacy Bee support if the problem persists</li>
                        <li>Your license remains active until the issue is resolved</li>
                    </ul>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <h4 class="card-title">Contact Support</h4>
                </div>
                <div class="card-content">
                    <p class="support-text">
                        If you continue to experience issues, our support team is here to help. 
                        Please contact us with details about your refund request.
                    </p>
                    <a href="mailto:support@privacybee.com" class="support-link">
                        support@privacybee.com
                    </a>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            <PrimeButton label="Try Again" variant="secondary" class="p-btn-root_secondary" @click="$emit('goToPage', 4)" />
            <PrimeButton label="Close" @click="$emit('close')" />
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    errorMessage: string;
    license?: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    close: [];
    goToPage: [page: number];
}>();
</script>

<style lang="scss" scoped>
.refund-error-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .error-icon {
        @apply flex justify-center mb-4;
    }
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
    }
}

.error-details {
    @apply mb-6 space-y-4;
}

.info-card {
    @apply bg-white rounded-lg border border-gray-200 p-6;
    
    &.error-card {
        @apply border-red-200 bg-red-50;
    }
    
    .card-header {
        @apply flex items-center mb-4;
        
        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }
    
    .card-content {
        .error-message {
            @apply text-red-800 font-medium bg-red-100 p-3 rounded border border-red-200;
            word-break: break-word;
        }
        
        .action-list {
            @apply list-disc list-inside space-y-2 text-gray-700;
        }
        
        .support-text {
            @apply text-gray-700 mb-3;
        }
        
        .support-link {
            @apply text-blue-600 font-medium hover:text-blue-700 hover:underline;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover:not(:disabled) {
                @apply border-gray-400 bg-gray-50;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
        
        &.btn-primary {
            @apply bg-blue-500 text-white border border-blue-500;
            
            &:hover:not(:disabled) {
                @apply bg-blue-600 border-blue-600;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
    }
}
</style>
